import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Skeleton — Studio Linen v1.0
 *
 * Building blocks for page-specific loading states.
 *
 *   <Skeleton width="60%" height={20} />
 *   <Skeleton shape="circle" size={32} />
 *   <Skeleton shape="block" height={200} />
 *
 * Use SkeletonStack for the generic 4-line placeholder (matches what
 * ProtectedRoute shows during auth check).
 */
export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: number | string;
  height?: number | string;
  shape?: 'line' | 'block' | 'circle' | 'square';
  /** for shape=circle/square */
  size?: number | string;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { width, height, shape = 'line', size, className, style, ...rest }, ref,
) {
  const cn = ['bq-skeleton', `bq-skeleton--${shape}`, className ?? ''].filter(Boolean).join(' ');
  const dim: CSSProperties = { ...style };
  if (shape === 'circle' || shape === 'square') {
    if (size != null) {
      dim.width = size;
      dim.height = size;
    }
  } else {
    if (width != null) dim.width = width;
    if (height != null) dim.height = height;
  }
  return <div ref={ref} className={cn} style={dim} aria-hidden="true" {...rest} />;
});

/**
 * Generic 4-line skeleton — title + 2 lines + block.
 * Pages with custom shape should NOT use this; build a page-specific
 * skeleton from <Skeleton> primitives.
 */
export function SkeletonStack({ children }: { children?: ReactNode }) {
  if (children) {
    return (
      <div className="bq-skeleton-stack" aria-busy="true" aria-live="polite">
        {children}
      </div>
    );
  }
  return (
    <div className="bq-skeleton-stack" aria-busy="true" aria-live="polite">
      <Skeleton width="40%" height={36} />
      <Skeleton width="100%" height={14} />
      <Skeleton width="70%" height={14} />
      <Skeleton shape="block" height={220} />
    </div>
  );
}
